import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label, ReferenceArea, ReferenceLine } from 'recharts';
import { convertSingleValue } from '../utils/commonUtils';

const MIN_ZOOM = 5; // adjust based on your data
const DEFAULT_ZOOM = { x1: null, y1: null, x2: null, y2: null };


const AnalysisScatterGraph = ({ data, height = 500 }) => {
    for (let i = 0; i < data.data.length ; i ++){
        data.data[i]['companyName'] = data.companies[i]['companyName'];
    }
    const color = [
        { fill: 'rgba(145, 186, 250, 1)', edge: 'rgba(80, 140, 200, 1)' }, // 青
        { fill: 'rgba(90, 195, 90, 1)', edge: 'rgba(50, 110, 50, 1)' }, // 緑
        { fill: 'rgba(225, 97, 50, 1)', edge: 'rgba(255, 120, 90, 1)' }, // オレンジ
        { fill: 'rgba(147, 112, 219, 1)', edge: 'rgba(75, 0, 130, 1)' },  // 紫
        { fill: 'rgba(255, 235, 59, 1)', edge: 'rgba(251, 192, 45, 1)' }, // 黄色
    ];

    // 軸の値を兆や億に変換するフォーマッター
    const formatTick = (tick) => {
        return convertSingleValue(tick);
    };

    const [filteredData, setFilteredData] = useState(data);
    // selected data point
    const [selectedPoint, setSelectedPoint] = useState(data[1]);
    // zoom coordinates
    const [zoomArea, setZoomArea] = useState(DEFAULT_ZOOM);
    // flag if currently zooming (press and drag)
    const [isZooming, setIsZooming] = useState(false);
    // flag if zoomed in
    const isZoomed = filteredData.data?.length !== data.data?.length;
    const [clickTimeout, setClickTimeout] = useState(null);
    // flag to show the zooming area (ReferenceArea)
    const showZoomBox =
        isZooming &&
        !(Math.abs(zoomArea.x1 - zoomArea.x2) < MIN_ZOOM) &&
        !(Math.abs(zoomArea.y1 - zoomArea.y2) < MIN_ZOOM);

    const [xRange, setXRange] = useState(0);
    const [yRange, setYRange] = useState(0);

    // reset the states on zoom out
    const handleZoomOut = () => {
        setFilteredData(data);
        setZoomArea(DEFAULT_ZOOM);
    }

    const handleMouseDown = (e) => {
        if (clickTimeout) {
            clearTimeout(clickTimeout);  // ダブルクリックを検出
            setClickTimeout(null);
            handleZoomOut();  // ダブルクリック時はズームアウト
        } else {
            const newTimeout = setTimeout(() => {
                setIsZooming(true);
                const { xValue, yValue } = e || {};
                setZoomArea({ x1: xValue, y1: yValue, x2: xValue, y2: yValue });
                setClickTimeout(null);
            }, 200);  // 200ms以内に2回クリックされたらダブルクリックとみなす
            setClickTimeout(newTimeout);
        }
    };

    useEffect(() => {
        setFilteredData(data);
    }, [data])

    // Update zoom end coordinates
    function handleMouseMove(e) {
    if (isZooming) {
        setZoomArea((prev) => ({ ...prev, x2: e?.xValue, y2: e?.yValue }));
    }
    }

    // When zooming stops, update with filtered data points
    // Ignore if not enough zoom
    function handleMouseUp(e) {
        if (isZooming) {
            setIsZooming(false);
            let { x1, y1, x2, y2 } = zoomArea;

            // ensure x1 <= x2 and y1 <= y2
            if (x1 > x2) [x1, x2] = [x2, x1];
            if (y1 > y2) [y1, y2] = [y2, y1];

            if (x2 - x1 < MIN_ZOOM || y2 - y1 < MIN_ZOOM) {
            } else {
            const dataPointsInRange = filterDataInRange(filteredData, x1, x2, y1, y2);
            setFilteredData(dataPointsInRange);
            setZoomArea(DEFAULT_ZOOM);
            }
        }
    }

    const filterDataInRange = (filteredData, x1, x2, y1, y2) => {
        // x, y の範囲で data をフィルタリング
        const filteredDataPoints = filteredData.data.filter(
            (d) => d.x >= x1 && d.x <= x2 && d.y >= y1 && d.y <= y2
        );
    
        // フィルタされた data に含まれる companyName に基づいて companies もフィルタリング
        const filteredCompanies = filteredData.companies.filter(company =>
            filteredDataPoints.some(dataPoint => dataPoint.companyName === company.companyName)
        );
    
        // フィルタされたデータを元の構造に合わせて返す
        return {
            data: filteredDataPoints,
            unit: filteredData.unit,
            name: filteredData.name,
            graphType: filteredData.graphType,
            companies: filteredCompanies
        };
    };

    const CustomMarker = (props) => {
        const { cx, cy, fill } = props;
        const size = 16; // マーカーのサイズを指定 (ピクセル単位)
        const strokeColor = color[0]['edge'];
        const strokeWidth = 1; // 外枠の太さ
        return (
            <circle
                cx={cx}
                cy={cy}
                r={size / 2}
                fill={fill}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
            />
        );
    };
        
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const { companyName, x, y, xTiming, yTiming } = payload[0].payload;
            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-400 bg-gray-600 rounded shadow text-white">
                    <div>{companyName}</div>
                    <div>{`${data.name[0]}: ${convertSingleValue(x)} (${xTiming})`}</div>
                    <div>{`${data.name[1]}: ${convertSingleValue(y)} (${yTiming})`}</div>
                </div>
            );
        }
        return null;
    };
    
      
    // xを数値に変換
    const cleanData = (data) => {
        return data.data.filter(item => 
            !isNaN(item.x) && !isNaN(item.y)
        );
    };
    

    // xRangeとyRangeを計算
    useEffect(() => {
        if (filteredData?.data?.length > 0) {
            const xValues = filteredData.data.map(d => d.x);
            const yValues = filteredData.data.map(d => d.y);

            const xMin = Math.min(...xValues);
            const xMax = Math.max(...xValues);
            const yMin = Math.min(...yValues);
            const yMax = Math.max(...yValues);

            setXRange((xMax - xMin) * 0.1);
            setYRange((yMax - yMin) * 0.1);
        }
    }, [filteredData]);

    return (
        <ResponsiveContainer width="100%" height={height}>
            <ScatterChart
                margin={{ top: 40, right: 5, left: 5, bottom: 40 }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            >
            <CartesianGrid strokeDasharray="3 3" />
            <ReferenceLine x={0} stroke="lightgrey" />
            {/* Y軸に0の基準線を追加 */}
            <ReferenceLine y={0} stroke="lightgrey" />

            <XAxis
                dataKey="x"
                type="number"
                tickFormatter={formatTick} // ラベルを兆や億で表示
                // domain={['auto', 'atuo']}
                domain={[`dataMin - ${xRange}`, `dataMax + ${xRange}`]}
                tick={{ fontSize: 12, fill: '#000' }}
            >
                <Label 
                    value={data.name[0]}
                    position="bottom" 
                    offset={10}
                    style={{ textAnchor: 'middle', fill: '#000' }}
                />
            </XAxis>
            <YAxis
                dataKey="y"
                type="number"
                tickFormatter={formatTick} // ラベルを兆や億で表示
                domain={[`dataMin - ${yRange}`, `dataMax + ${yRange}`]}
                tick={{ fontSize: 12, fill: '#000' }}
            >
                <Label 
                    value={data.name[1]}
                    position="top" 
                    offset={20} 
                    angle={0}
                    style={{ textAnchor: 'middle', fill: '#000' }}
                />
            </YAxis>
            <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(200, 200, 200, 0.5)' }}
            />
            {showZoomBox && (
                <ReferenceArea
                    x1={zoomArea?.x1}
                    x2={zoomArea?.x2}
                    y1={zoomArea?.y1}
                    y2={zoomArea?.y2}
                />
                )}
            <Scatter
                data={cleanData(filteredData)}
                fill={color[0]['fill']}
                shape={<CustomMarker />}
                />
            </ScatterChart>
        </ResponsiveContainer>
    );
};

export default AnalysisScatterGraph;
